import { NavigableButton } from '@carvana/ds-button';
import { Option } from '@carvana/ds-popup';
import Select from '@carvana/ds-select';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { NavPayloadAction } from '@carvana/merch-native';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useMemo, useState } from 'react';
import { ANALYTICS_EVENTS } from '~/constants/flp/analyticsEvents';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { useFinancingContext } from '~/context/flp/FinancingContext';
import { getCvnaIdFinanceFilters } from '~/utilities/flp/getCvnaId';
import { Dropdowns } from './ShopPaymentForm.styles';

const DOWN_PAYMENT_ID = 'downPayment';
const MONTHLY_PAYMENT_ID = 'monthlyPayment';

const ShopPaymentForm = () => {
  const { aggregates } = useFinancingContext();
  const { trackEvent } = useContext(AnalyticsContext);
  const downPaymentOptions = aggregates?.downPaymentDropdownOptions ?? [];
  const monthlyPaymentOptions = aggregates?.monthlyPaymentDropdownOptions ?? [];

  const [downPayment, setDownPayment] = useState(
    downPaymentOptions[Math.floor(downPaymentOptions.length / 2)]
  );
  const [monthlyPayment, setMonthlyPayment] = useState(
    monthlyPaymentOptions[Math.floor(monthlyPaymentOptions.length / 2)]
  );

  const url = useMemo(() => {
    const cvnaid = getCvnaIdFinanceFilters({ downPayment, monthlyPayment });

    return `/cars/filters?cvnaid=${cvnaid}&refSource=financing`;
  }, [downPayment, monthlyPayment]);

  const onDownPaymentChange = (
    _children: any,
    _event: any,
    value?: string | string[]
  ) => {
    const newValue = Number(value);
    if (isNaN(newValue)) return;
    setDownPayment(newValue);
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.SETS_SHOPBYPAYMENT, {
      field: DOWN_PAYMENT_ID,
      downPayment: newValue,
      monthlyPayment
    });
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.SETS_SHOPBYPAYMENT, {
      field: DOWN_PAYMENT_ID,
      downPayment: newValue,
      monthlyPayment
    });
  };

  const onMonthlyPaymentChange = (
    _children: any,
    _event: any,
    value?: string | string[]
  ) => {
    const newValue = Number(value);
    if (isNaN(newValue)) return;
    setMonthlyPayment(newValue);
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.SETS_SHOPBYPAYMENT, {
      field: MONTHLY_PAYMENT_ID,
      downPayment,
      monthlyPayment: newValue
    });
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.SETS_SHOPBYPAYMENT, {
      field: MONTHLY_PAYMENT_ID,
      downPayment,
      monthlyPayment: newValue
    });
  };

  const onSubmit = () => {
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.CLICKS_SHOPBYPAYMENT_CTA, {
      downPayment,
      monthlyPayment
    });
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_SHOPBYPAYMENT_CTA, {
      downPayment,
      monthlyPayment
    });
  };

  return (
    <>
      <Dropdowns>
        <Select
          id={DOWN_PAYMENT_ID}
          label="Max cash down"
          value={`$${Number(downPayment).toLocaleString()}`}
          onChange={onDownPaymentChange}
        >
          {downPaymentOptions?.map(option => (
            <Option key={`downPayment_${option}`} value={`${option}`}>
              ${option?.toLocaleString()}
            </Option>
          ))}
        </Select>
        <Select
          id={MONTHLY_PAYMENT_ID}
          label="Max monthly payment"
          value={`$${Number(monthlyPayment).toLocaleString()}/mo`}
          onChange={onMonthlyPaymentChange}
        >
          {monthlyPaymentOptions?.map(option => (
            <Option key={`monthlyPayment_${option}`} value={`${option}`}>
              ${option?.toLocaleString()}
            </Option>
          ))}
        </Select>
      </Dropdowns>
      <NavigableButton
        className="sm:self-start"
        data-analytics-id="Financing Shop Vehicles Button"
        data-native-payload={JSON.stringify({
          action: NavPayloadAction.ViewSrp
        })}
        href={url}
        size="lg"
        variant="secondary"
        onClick={onSubmit}
        data-testid="shop-vehicles"
      >
        Shop Vehicles
      </NavigableButton>
    </>
  );
};

export default ShopPaymentForm;
